import { GraphTableProperty } from "@dto/pageResponse.dto";
import { TimeseriesMetadata } from "@dto/timeseriesResponse.dto";
import getPathValue from '@rc-component/util/es/utils/get';
import { TablePaginationConfig } from "antd";
import { CompareFn, FilterValue, SortOrder, SorterResult, TableCurrentDataSource } from "antd/es/table/interface";
import { get } from "lodash-es";
import type { ScatterData } from "plotly.js";
import { FC, useCallback, useContext, useState } from "react";
import { GLOB } from "src/util/Glob";
import { TextUtil } from "src/util/TextUtil";
import { UnitHelper } from "src/util/UnitHelper";
import { nameof } from 'ts-simple-nameof';
import { XmLink } from "../link/XmLink";
import { XmTableScope } from "../table/XmTable";
import { ColGroupProps, ColProps, Plain, XmColumnsType, XmTableEntry } from "../table/tableTypes";
import { GraphPlotUtil } from "./GraphPlotUtil";
import { CommonGraphProps, GraphProps, GraphState, KTM, RegroupContext, RegroupProps, UidName } from "./GraphTypes";
import { COLOR_SQUARE_WIDTH, XmColorSquare } from "./XmColorSquare";

const cols = [
  {
    key: 'avg',
    title: 'Avg',
  },
  {
    key: 'max',
    title: 'Max',
  },
];

function sortCommon(l: KTM, r: KTM, gpu: GraphPlotUtil, sortOrder?: SortOrder) {
  if (l.isLast() || r.isFirst()) {
    return sortOrder === 'ascend' ? 1 : -1;
  }
  if (r.isLast() || l.isFirst()) {
    return sortOrder === 'ascend' ? -1 : 1;
  }

  const mCmp = gpu.cmpKtmIfMetric(l, r);
  if (mCmp != null) return 0;
}

function numericSort(metric: string, col: string, gpu: GraphPlotUtil): CompareFn<KTM> {
  return (l: KTM, r: KTM, sortOrder?: SortOrder) => {
    const common = sortCommon(l, r, gpu, sortOrder);
    if (common != null) return common;

    const left = l.data[metric][col];
    const right = r.data[metric][col];
    if (left == null) {
      return sortOrder === 'ascend' ? 1 : -1;
    }
    if (right == null) {
      return sortOrder === 'ascend' ? -1 : 1;
    }
    return left - right;
  };
};

function stringSort(selector: (props: KTM) => unknown, gpu: GraphPlotUtil): CompareFn<KTM> {
  return (l: KTM, r: KTM, sortOrder?: SortOrder) => {
    const common = sortCommon(l, r, gpu, sortOrder);
    if (common != null) return common;

    const path = nameof(selector);
    const a = get(l, path) as string;
    if (a == null) return sortOrder === 'ascend' ? 1 : -1;
    const b = get(r, path) as string;
    if (b == null) return sortOrder === 'ascend' ? -1 : 1;
    return GLOB.naturalSort(a, b);
  };
}

interface GraphTableBaseProps {
  data: Partial<ScatterData>[];
  tableData: KTM[];

  refreshPlots?: () => void;
  setPlotVisible: (visible: boolean) => void;
  setData: React.Dispatch<React.SetStateAction<Partial<ScatterData>[]>>;
  setTableData: (tableData: KTM[]) => void;
  assignTickFormat: (tblData: KTM[], plotData?: Partial<ScatterData>[]) => void;
}

interface GraphTableProps extends GraphTableBaseProps {
  graphPlotData: GraphPlotUtil;
  state: GraphState;
  //box: GraphBase;
  graphProps: GraphProps;
  assignFixedUIDs: () => void;
}

export interface ColorSquareParams extends GraphTableBaseProps {
  regroupedProps?: RegroupProps;
  assignFixedUIDs?: () => void;
}

/**
 * Create clickable color square to control plot visibility
 * @param ktm
 * @param metricName
 * @param params
 * @returns element
 */
export function colorSquare(ktm: Plain<KTM>, metricName: string, params: ColorSquareParams) {

  if (KTM.isTotal(ktm)) return;

  function reloadPlot() {
    // Workaround for plotly missing trace on restore visibility
    params.setPlotVisible(false);
    setTimeout(() => {
      params.setPlotVisible(true);
      setTimeout(() => {
        params.setTableData([...params.tableData]);
      });
    });
  }

  // every trace carries its color, see GraphPlotUtil.traceColor
  const dat = params.data.find(GraphPlotUtil.traceMatcher(ktm.uid, metricName));
  const color = dat?.line?.color as string;
  if (!color) return;
  const regroupedProps = params.regroupedProps;
  const hasAreas = () => params.data.some(GraphPlotUtil.isAreaTrace);

  function commit() {
    params.assignFixedUIDs?.();
    params.assignTickFormat(params.tableData, params.data);
    params.setData([...params.data]);
    params.setTableData([...params.tableData]);
    params.refreshPlots?.();
  }
  return <XmColorSquare color={color} visible={dat.visible === true}
    tooltip={<span>Click to show/hide this trace<br />Double-click to show/hide other traces.</span>}
    onClick={() => {
      if (GraphPlotUtil.isUserHidden(dat)) {
        dat.visible = true;
        if (hasAreas())
          reloadPlot();
        regroupedProps?.setHiddenItems(regroupedProps.hiddenItems.filter(um => um !== ktm.uid));
      } else {
        dat.visible = 'legendonly';
        regroupedProps?.setHiddenItems([...regroupedProps.hiddenItems, ktm.uid]);
      }
      commit();
    }}
    onDoubleClick={() => {
      if (dat.visible === true && !params.data.some(GraphPlotUtil.isUserHidden)) {
        params.data.filter(pd => pd.visible).forEach(ppd => ppd.visible = 'legendonly');
        dat.visible = true;
        regroupedProps?.setHiddenItems(params.data.filter(pd => pd !== dat).map(pd => pd.meta?.uid as string));
      } else {
        params.data.filter(ppd => ppd.visible).forEach(ppd => ppd.visible = true);
        if (hasAreas())
          reloadPlot();
        regroupedProps?.setHiddenItems([]);
      }
      commit();
    }} />;
}

export const GraphTable: FC<GraphTableProps> = (props) => {
  const { graphPlotData, data, setData, refreshPlots, tableData, setTableData, state,
    graphProps: { box, common = {} }, assignTickFormat, assignFixedUIDs } = props;
  const [tscope] = useState(() => new XmTableScope<KTM>("plain"));
  const regroupedProps = useContext(RegroupContext);

  /**
   * Sync trace visibility with the rows the table filter lets through.
   * Filtered-out traces are `false`; square-click `legendonly` is kept so clearing the filter does not unhide them,
   * and a trace that was hidden in all graphs while filtered out comes back as `legendonly`.
   * @param shownRows rows currently displayed by the table
   */
  function applyTableFilter(shownRows: readonly KTM[]) {
    const uids = new Set(shownRows.map(ktm => ktm.uid));
    const hidden = new Set(regroupedProps.hiddenItems);
    let changed = false;
    for (const pd of data) {
      if (KTM.isTotal(pd.meta as UidName) || GraphPlotUtil.isUserHidden(pd)) continue;
      const uid = pd.meta?.uid as string;
      const visible: ScatterData['visible'] = !uids.has(uid) ? false : hidden.has(uid) ? 'legendonly' : true;
      if (pd.visible !== visible) {
        pd.visible = visible;
        changed = true;
      }
    }
    if (!changed) return;
    setData([...data]);
    refreshPlots?.();
  }

  const preRendered = (path: readonly (string | number)[]) => (value, record: TimeseriesMetadata & XmTableEntry) =>
    getPathValue(record.rendered, path) ?? getPathValue(record.rendered, [GraphPlotUtil.SINGLE_ITEM, ...path.slice(1)]);

  const onGridChange = (pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>,
    sorter: SorterResult<KTM>, extra: TableCurrentDataSource<KTM>) => {
    if (extra.action === 'filter') {
      applyTableFilter(extra.currentDataSource);
    } else if (extra.action === 'sort') {
      graphPlotData.userSort.col = sorter.field as ColProps<KTM>['dataIndex'];
      graphPlotData.userSort.order = sorter.order;
    }
  };

  function tableSummaryData(currentData: (KTM & XmTableEntry)[]) {
    // workaround to get sorted data
    if (!state.tableRowKeys.length) {
      state.tableRowKeys = currentData.map(ktm => ktm.key);
      return null;
    }
    let j = 0;
    for (const currentRow of currentData) {
      let found = false;
      for (; j < state.tableRowKeys.length; j++) {
        if (currentRow.key === state.tableRowKeys[j]) {
          found = true;
          break;
        }
      }
      if (!found) {
        state.tableRowKeys = currentData.map(ktm => ktm.key);
        graphPlotData.sortPlotByTable(data, currentData);
        GraphPlotUtil.liftLinesAboveAreas(data);
        setTimeout(() => {
          setData([...data]);
          //refreshPlots?.();
        });
        break;
      }
    }
    return null;
  }

  function tableFooter() {
    const returnedCount = graphPlotData.itemsCount.others + graphPlotData.itemsCount.shown;
    const loaded = Math.min(state.loadedItems, returnedCount);
    // footer is present if assigned a function even it returns undefined
    return returnedCount === loaded
      || box.skipNull
      || tableData.some(ktm => ktm.isOthers())
      || !GraphPlotUtil.isOthersAllowed(box) ? undefined
      : () => `Showing top ${graphPlotData.itemsCount.shown} items of ${returnedCount}`;
  }

  const propertyColumn = useCallback(propertyColumnMake(tscope, common), [tscope]);

  const colorParams: ColorSquareParams = {
    data, tableData, setData, setTableData, refreshPlots, assignTickFormat, setPlotVisible: props.setPlotVisible, regroupedProps, assignFixedUIDs
  };
  // the graph's own metrics win over the subsystem-wide list on a shared key
  const metricLabels = new Map<string, string>();
  for (const m of [...box.metrics, ...(common.availableMetrics || []), ...(state.peakMetric ? [box.peakMetric] : [])]) {
    if (!metricLabels.has(m.metric)) metricLabels.set(m.metric, m.label);
  }

  // columns before the first color square; sized to content, then they split the remaining
  // container width in proportion to that size
  const leadingColumns: ColProps<KTM>[] = ([
    {
      key: 'name',
      className: "xm-td-ellipsis",
      title: `${graphPlotData.isMetricItems() ? UnitHelper.formatUnitColHeader('', state.unitSize)
        : graphPlotData.isMixedMetricItem() ? UnitHelper.formatUnitColHeader(box.label || "Name", state.unitSize) : (box.label || "Name")}`,
      dataIndex: "name",
      sorter: stringSort(t => t.name, graphPlotData),
      filter: { field: ['name'] },
      render: (value, record) => {
        const name = metricLabels.get(value) || value;
        const metricKey = graphPlotData.isSingleItem() ? GraphPlotUtil.SINGLE_ITEM : box.metrics[0].metric;
        const url = record.data[metricKey]?.url;
        if (!box.aggregated || !url)
          if (KTM.isOthers(record.name, record.uid))
            return <div className='xm-ellipsis' title={state.othersTooltip}>
              {`${name} (${state.othersCount} of ${graphPlotData.itemsCount.others + graphPlotData.itemsCount.shown})`}
            </div>;
          else
            return <div className='xm-ellipsis'><div>{name}</div></div>;

        return <div className='xm-ellipsis'><XmLink to={GLOB.menuService.getRouteLink(url, common?.tabName, common?.subTabName)}>{name}</XmLink></div>;
      }
    },

    box.parent && !graphPlotData.isMetricItems() && {
      key: "parent",
      className: 'xm-td-ellipsis',
      title: box.parent.label,
      dataIndex: ["parent", 'label'],
      sorter: stringSort(t => t.parent.label, graphPlotData),
      filter: { field: ['parent', 'label'] },
      render: (value, record) => {
        if (!record.parent) return null;
        const label = record.parent.label;
        const url = record.parent.url;
        if (url)
          return <div className='xm-ellipsis'><XmLink to={GLOB.menuService.getRouteLink(url, common.tabName)}>{label}</XmLink></div>;
        return <div className='xm-ellipsis'><div>{label}</div></div>;
      }
    },

    ...(box.properties?.map(propertyColumn) || []),

    graphPlotData.isSingleItem() && !graphPlotData.isMetricItems() && box.metrics.length > GraphPlotUtil.MAX_GROUP_METRICS && !graphPlotData.isMixedMetricItem() && {
      key: "metric",
      className: "xm-td-ellipsis",
      title: UnitHelper.formatUnitColHeader('Metric', state.unitSize),
      dataIndex: "metric",
      sorter: stringSort(t => t.metric, graphPlotData),
      filter: { field: ['metric'] }
    },
  ] as ColProps<KTM>[]).filter(Boolean);
  leadingColumns.forEach(c => c.grow = true);

  const valueColumns = (metric: string): ColProps<KTM>[] => cols.map((c) => ({
    ...c,
    key: metric === GraphPlotUtil.SINGLE_ITEM ? c.key : metric + c.key,
    dataIndex: ['data', metric, c.key],
    sorter: numericSort(metric, c.key, graphPlotData),
    sortDirections: ['descend', 'ascend'],
    filter: { field: ['rendered', metric, c.key], type: 'number' },
    render: preRendered([metric, c.key])
  }));

  const columns: XmColumnsType<KTM> = [
    ...leadingColumns,

    ...(graphPlotData.isSingleItem()
      ? [
        {
          key: 'metricColor',
          className: 'square-td',
          width: COLOR_SQUARE_WIDTH,
          render: (value, record) => colorSquare(record, undefined, colorParams)
        } as ColProps<KTM>,
        ...valueColumns(GraphPlotUtil.SINGLE_ITEM)
      ]
      : box.metrics.filter(m => !graphPlotData.isMetricItems(m)).map<ColGroupProps<KTM>>((m) => ({
        key: m.metric,
        title: UnitHelper.formatUnitColHeader(m.label, state.unitSize),
        children: [
          {
            key: m.metric + 'Color',
            className: 'square-td',
            width: COLOR_SQUARE_WIDTH,
            render: (value, record) =>
              colorSquare(record, record.rendered[GraphPlotUtil.SINGLE_ITEM] ? GraphPlotUtil.SINGLE_ITEM : m.metric, colorParams)
          },
          ...valueColumns(m.metric)
        ]
      }))),
  ];

  return <tscope.XmTable
    key='graph-grid'
    className="xm-graph-table"
    dataSource={tableData}
    columns={columns}
    onChange={onGridChange}
    onFilteredDataChange={applyTableFilter}
    tableLayout="fixed"
    widthOptions={{ fontScale: 0.9, controlGap: 0, mode: 'fill' }}
    scroll={{ y: 90 }}
    pagination={false}
    bordered={true}
    summary={tableSummaryData}
    footer={tableFooter()}
    rowClassName={(record) => record.props?.separator ? 'xm-tr-separator' : ''}
  >
  </tscope.XmTable>;
};

function propertyColumnMake(tscope: XmTableScope<KTM>, common: CommonGraphProps) {
  return function propertyColumn(gtp: GraphTableProperty): ColProps<KTM> {
    return {
      className: "xm-col-prop",
      key: gtp.label,
      dataIndex: ['props', 'itemProps', gtp.label, 'value'],
      title: gtp.label,
      sorter: (a, b) => gtp.values[a.uid].value.localeCompare(gtp.values[b.uid].value),
      filter: gtp.search ? { field: ['props', 'itemProps', gtp.label, 'value'] } : undefined,
      render: (value: string, record) => {
        if (!value) return;
        const item = record.props.itemProps[gtp.label];
        const text = TextUtil.trimMiddle(item.value, 24);
        return item.url ? <XmLink to={GLOB.menuService.getRouteLink(item.url, common?.tabName)}>{text}</XmLink> : text;
      }
    };
  };
}